import { useEffect, useRef, useState } from "react";
import { ChevronRight, Pencil, Plus, ShieldCheck, Trash2 } from "lucide-react";
import { http, roleLabels, loanTypes, notify } from "../api";
import { Loading, Modal, PanelHead } from "./common";
import { CallbacksTab, CampaignsTab, SaApprovalsTab, ScriptsTab } from "./SaAdmin";
import { CampaignSyncDiagnosticTab, IntegrationHealthTab, LeadBulkImportTab } from "./SyncAdmin";
import { PoliciesTab } from "./PoliciesTab";

const roles = [
  "admin", "sub_admin", "business_head", "sales_manager",
  "telecaller",
  "back_office_manager", "back_office_executive",
];

export default function AdminPanel({ user: me }) {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("users");

  const load = () =>
    http.get("/admin/config")
      .then((r) => setData(r.data))
      .catch((err) => notify.error(err, "Could not load admin controls"));

  // NOTE (QA fix): `load` is a concise arrow returning a Promise. Passing it directly to
  // useEffect made React treat the Promise as the cleanup fn -> "destroy is not a function"
  // runtime crash that blanked the whole Admin Panel. Wrapped so nothing is returned.
  useEffect(() => {
    load();
  }, []);

  if (!data) return <Loading>Loading admin controls…</Loading>;

  return (
    <>
      <div className="page-intro">
        <div>
          <p className="eyebrow">ADMINISTRATION / CONTROL PLANE</p>
          <h1>Configuration</h1>
          <p className="muted">Users, teams, partner banks & products, statuses and permissions.</p>
        </div>
        <span className="scope-chip"><ShieldCheck size={14} /> Admin only</span>
      </div>

      <div className="admin-tabs">
        {[
          ["users", "Users & permissions"],
          ["sa_approvals", "SA approvals"],
          ["callbacks", "Callbacks"],
          ["teams", "Teams"],
          ["partners", "Banks & products"],
          ["policies", "Policies"],
          ["statuses", "Statuses & dispositions"],
          ["loadboard", "Bank load board"],
          ["companies", "Companies"],
          ["lead_import", "Bulk import"],
          ["campaigns", "Campaigns"],
          ["scripts", "Scripts"],
          ["integration", "Integration health"],
          ["sync_diag", "Sync diagnostic"],
          ["digest", "Morning digest"],
        ].map(([id, label]) => (
          <button
            key={id}
            data-testid={`admin-${id}-tab`}
            className={tab === id ? "active" : ""}
            onClick={() => setTab(id)}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === "users" && <UsersTab data={data} reload={load} me={me} />}
      {tab === "sa_approvals" && <SaApprovalsTab me={me} teams={data.teams} />}
      {tab === "callbacks" && <CallbacksTab />}
      {tab === "teams" && <TeamsTab data={data} reload={load} />}
      {tab === "partners" && <PartnersTab data={data} reload={load} />}
      {tab === "policies" && <PoliciesTab data={data} me={me} />}
      {tab === "statuses" && <StatusesTab data={data} reload={load} />}
      {tab === "loadboard" && <LoadBoardTab />}
      {tab === "companies" && <CompaniesTab me={me} data={data} />}
      {tab === "lead_import" && <LeadBulkImportTab />}
      {tab === "campaigns" && <CampaignsTab />}
      {tab === "scripts" && <ScriptsTab />}
      {tab === "integration" && <IntegrationHealthTab />}
      {tab === "sync_diag" && <CampaignSyncDiagnosticTab />}
      {tab === "digest" && <DigestTab />}
    </>
  );
}

/* ---------- Users & permissions ---------- */
export function UsersTab({ data, reload, me }) {
  const [selected, setSelected] = useState(null);
  const [creating, setCreating] = useState(false);

  const toggle = async (permission) => {
    const user = data.users.find((x) => x.id === selected);
    const next = (user.permissions || []).includes(permission)
      ? user.permissions.filter((x) => x !== permission)
      : [...(user.permissions || []), permission];
    try {
      await http.patch(`/admin/users/${selected}`, { permissions: next });
      notify.success("Permission updated");
      reload();
    } catch (err) {
      notify.error(err, "Could not update permission");
    }
  };

  const toggleActive = async (u) => {
    try {
      await http.patch(`/admin/users/${u.id}`, { active: !u.active });
      notify.success(`${u.name} ${u.active ? "deactivated" : "activated"}`);
      reload();
    } catch (err) {
      notify.error(err, "Could not update user");
    }
  };

  const resetPassword = async (u) => {
    if (!window.confirm(
      `Reset password for ${u.name}?\n\nA temporary password will be generated. ` +
      `The user will be forced to change it on their next login. ` +
      `Their current password will immediately stop working.`,
    )) return;
    try {
      const r = await http.post(`/admin/users/${u.id}/reset-password`);
      // Copy to clipboard for easy hand-off.
      try { await navigator.clipboard.writeText(r.data.new_password); } catch {}
      window.prompt(
        `Temporary password (copied to clipboard):\n\n` +
        `${u.employee_code || u.email} · ${r.data.new_password}\n\n` +
        `Share it with the user securely. This is the only time you'll see it.`,
        r.data.new_password,
      );
      notify.success("Password reset — user must change it on next login");
    } catch (err) {
      notify.error(err, "Reset failed");
    }
  };

  const updateEmployeeCode = async (u) => {
    const next = window.prompt(
      `Set login Employee ID for ${u.name}. Must be unique.\n\nCurrent: ${u.employee_code || "(not set)"}`,
      u.employee_code || "",
    );
    if (next === null) return;
    const code = next.trim().toUpperCase();
    if (!code) return notify.error("Employee ID cannot be blank");
    try {
      await http.patch(`/admin/users/${u.id}`, { employee_code: code });
      notify.success(`Employee ID set to ${code}`);
      reload();
    } catch (err) {
      notify.error(err, "Could not update Employee ID");
    }
  };

  const deleteUser = async (u) => {
    const confirm = window.prompt(
      `Permanently delete ${u.name}? This cannot be undone.\n\n` +
      `Type "${u.email}" to confirm:`,
    );
    if (!confirm || confirm.trim() !== u.email) {
      if (confirm !== null) notify.error("Email did not match — nothing deleted");
      return;
    }
    try {
      await http.delete(`/admin/users/${u.id}`);
      notify.success(`${u.name} deleted`);
      setSelected(null);
      reload();
    } catch (err) {
      notify.error(err, "Could not delete user");
    }
  };

  return (
    <div className="admin-layout">
      <section className="panel">
        <div className="section-title">
          <h3>Team directory</h3>
          <button className="outline-button" onClick={() => setCreating(true)} data-testid="admin-add-user-button">
            <Plus size={15} /> New user
          </button>
        </div>
        {data.users.map((x) => (
          <button
            data-testid={`admin-user-${x.id}`}
            className={selected === x.id ? "admin-user selected" : "admin-user"}
            key={x.id}
            onClick={() => setSelected(x.id)}
          >
            <span className="avatar">{x.name.slice(0, 2).toUpperCase()}</span>
            <span>
              <strong>{x.name}</strong>
              <small>{roleLabels[x.role]} · {x.team_name} {x.active === false ? "· INACTIVE" : ""}</small>
            </span>
            <ChevronRight size={15} />
          </button>
        ))}
      </section>

      <section className="panel permission-panel">
        {selected ? (
          (() => {
            const u = data.users.find((x) => x.id === selected);
            return (
              <>
                <p className="eyebrow">PERMISSION SET</p>
                <h3>{u.name}</h3>
                <p className="muted">
                  Role: {roleLabels[u.role]} · {u.team_name}. Toggle access without changing their team boundary.
                </p>
                <p className="muted" style={{ fontSize: 12 }}>
                  <strong>Login Employee ID:</strong>{" "}
                  <span data-testid={`admin-user-empcode-${u.id}`}>
                    {u.employee_code || <em style={{ color: "var(--red)" }}>Not set</em>}
                  </span>
                </p>
                <div className="permission-toolbar">
                  <button
                    className="outline-button"
                    data-testid={`admin-user-empcode-edit-${u.id}`}
                    onClick={() => updateEmployeeCode(u)}
                  >
                    {u.employee_code ? "Edit Employee ID" : "Set Employee ID"}
                  </button>
                  <button className="outline-button" onClick={() => toggleActive(u)}>
                    {u.active === false ? "Reactivate user" : "Deactivate user"}
                  </button>
                  <button className="outline-button" onClick={() => resetPassword(u)}>
                    Reset password
                  </button>
                  {u.id !== me.id && (
                    <button
                      className="outline-button danger-button"
                      onClick={() => deleteUser(u)}
                      data-testid={`admin-user-delete-${u.id}`}
                      title="Permanently delete this account"
                    >
                      <Trash2 size={13} /> Delete permanently
                    </button>
                  )}
                </div>
                <div className="permission-grid">
                  {data.permissions.map((p) => (
                    <button
                      data-testid={`permission-${p}`}
                      className={(u.permissions || []).includes(p) ? "permission on" : "permission"}
                      key={p}
                      onClick={() => toggle(p)}
                    >
                      {(u.permissions || []).includes(p) ? "✓" : "＋"} {p.replaceAll("_", " ")}
                    </button>
                  ))}
                </div>
              </>
            );
          })()
        ) : (
          <div className="empty">Select a user to edit permissions.</div>
        )}
      </section>

      {creating && (
        <UserCreateModal
          teams={data.teams}
          onClose={() => setCreating(false)}
          onCreated={() => { setCreating(false); reload(); }}
        />
      )}
    </div>
  );
}

function UserCreateModal({ teams, onClose, onCreated }) {
  const [form, setForm] = useState({
    name: "", email: "", employee_code: "", mobile: "", address: "",
    role: "sales_manager",
    team_id: "", team_name: "", back_office_team_id: "",
    password: "LoanCRM2026!",
  });
  const [busy, setBusy] = useState(false);
  const salesTeams = teams.filter((t) => t.team_type === "sales");
  const backTeams = teams.filter((t) => t.team_type === "back_office");
  const isBack = form.role.startsWith("back_office");
  const needsSalesTeam = form.role === "sales_manager" || form.role === "telecaller";

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const team = teams.find((t) => t.team_id === form.team_id || t.team_id === form.back_office_team_id);
      await http.post("/admin/users", {
        ...form,
        employee_code: form.employee_code.trim().toUpperCase(),
        mobile: form.mobile.replace(/\D/g, ""),
        team_name: team?.name || "Unassigned",
      });
      notify.success("User created — share the employee ID + password with them");
      onCreated();
    } catch (err) {
      notify.error(err, "Could not create user");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="modal-backdrop" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <section className="modal">
        <div className="modal-head"><h2>Create user</h2><button className="icon-button" onClick={onClose}>✕</button></div>
        <form className="record-form" onSubmit={submit}>
          <div className="form-grid">
            <label>Full name<input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="admin-user-name-input" /></label>
            <label>
              Employee ID (login user id)
              <input
                required
                data-testid="admin-user-empcode-input"
                placeholder="e.g. RXSA00125"
                value={form.employee_code}
                onChange={(e) => setForm({ ...form, employee_code: e.target.value.toUpperCase() })}
                style={{ textTransform: "uppercase" }}
              />
            </label>
            <label>Mobile<input value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} data-testid="admin-user-mobile-input" /></label>
            <label>Email<input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="admin-user-email-input" /></label>
            <label style={{ gridColumn: "1 / -1" }}>Address<textarea rows={2} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="admin-user-address-input" /></label>
            <label>
              Role
              <select data-testid="admin-user-role-select" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
                {roles.map((r) => <option key={r} value={r}>{roleLabels[r]}</option>)}
              </select>
            </label>
            {needsSalesTeam && (
              <label>
                Sales team
                <select value={form.team_id} onChange={(e) => setForm({ ...form, team_id: e.target.value, back_office_team_id: "" })} data-testid="admin-user-team-select">
                  <option value="">Unassigned</option>
                  {salesTeams.map((t) => <option key={t.team_id} value={t.team_id}>{t.name}</option>)}
                </select>
              </label>
            )}
            {isBack && (
              <label>
                Back-office team
                <select value={form.back_office_team_id} onChange={(e) => setForm({ ...form, back_office_team_id: e.target.value, team_id: "" })} data-testid="admin-user-back-team-select">
                  <option value="">Unassigned</option>
                  {backTeams.map((t) => <option key={t.team_id} value={t.team_id}>{t.name}</option>)}
                </select>
              </label>
            )}
            <label>Initial password<input required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="admin-user-password-input" /></label>
          </div>
          <div className="form-actions">
            <button type="button" className="outline-button" onClick={onClose}>Cancel</button>
            <button className="primary-button" disabled={busy} data-testid="admin-user-submit-button">
              {busy ? "Creating…" : "Create user"}
            </button>
          </div>
        </form>
      </section>
    </div>
  );
}

/* ---------- Teams ---------- */
function TeamsTab({ data, reload }) {
  const [name, setName] = useState("");
  const [type, setType] = useState("sales");

  const add = async () => {
    if (!name.trim()) return;
    try {
      await http.post("/admin/teams", { name: name.trim(), team_type: type });
      notify.success("Team created");
      setName("");
      reload();
    } catch (err) {
      notify.error(err, "Could not create team");
    }
  };

  return (
    <section className="panel config-list">
      <div className="section-title">
        <h3>Teams</h3>
        <span className="mono">{data.teams.length} configured</span>
      </div>
      <div className="config-add">
        <input
          data-testid="admin-team-add-button-input"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="New team name"
        />
        <select value={type} onChange={(e) => setType(e.target.value)} data-testid="admin-team-type-select">
          <option value="sales">Sales</option>
          <option value="back_office">Back office</option>
        </select>
        <button className="primary-button" onClick={add} data-testid="admin-team-add-button">
          <Plus size={15} /> Add team
        </button>
      </div>
      {data.teams.map((x) => (
        <div className="config-row" key={x.team_id} data-testid={`admin-team-row-${x.team_id}`}>
          <div>
            <strong>{x.name}</strong>
            <small>{x.team_type === "back_office" ? "Back-office" : "Sales"} · {x.team_id}</small>
          </div>
        </div>
      ))}
    </section>
  );
}

/* ---------- Banks & Products ---------- */
function PartnersTab({ data, reload }) {
  const [bankForm, setBankForm] = useState({ name: "", default_commission_percent: "" });
  const [product, setProduct] = useState({ name: "", bank_id: "", loan_type: "Personal Loan" });
  const [editingBank, setEditingBank] = useState(null);
  const [editingProduct, setEditingProduct] = useState(null);

  const addBank = async () => {
    if (!bankForm.name.trim()) return;
    try {
      await http.post("/admin/banks", {
        name: bankForm.name.trim(),
        default_commission_percent: Number(bankForm.default_commission_percent) || 0,
      });
      notify.success("Bank added");
      setBankForm({ name: "", default_commission_percent: "" });
      reload();
    } catch (err) {
      notify.error(err, "Could not add bank");
    }
  };

  const deleteBank = async (b) => {
    if (!window.confirm(`Delete ${b.name}? Existing leads/applications retain their history.`)) return;
    try {
      const r = await http.delete(`/admin/banks/${b.bank_id}`);
      notify.success(r.data.soft_deleted ? `${b.name} deactivated (in-use)` : `${b.name} deleted`);
      reload();
    } catch (err) {
      notify.error(err, "Could not delete bank");
    }
  };

  const addProduct = async () => {
    if (!product.name.trim() || !product.bank_id) return;
    const bank = data.banks.find((b) => b.bank_id === product.bank_id);
    try {
      await http.post("/admin/products", {
        name: product.name.trim(),
        bank_id: product.bank_id,
        bank_name: bank?.name || "",
        loan_type: product.loan_type,
      });
      notify.success("Product added");
      setProduct({ name: "", bank_id: "", loan_type: "Personal Loan" });
      reload();
    } catch (err) {
      notify.error(err, "Could not add product");
    }
  };

  const deleteProduct = async (p) => {
    if (!window.confirm(`Delete ${p.name}?`)) return;
    try {
      const r = await http.delete(`/admin/products/${p.product_id}`);
      notify.success(r.data.soft_deleted ? "Deactivated (in use)" : "Product deleted");
      reload();
    } catch (err) {
      notify.error(err, "Could not delete product");
    }
  };

  return (
    <div className="config-columns">
      <section className="panel config-list">
        <div className="section-title">
          <h3>Banks / NBFCs</h3>
          <span className="mono">{data.banks.length}</span>
        </div>
        <div className="config-add">
          <input
            data-testid="admin-bank-add-button-input"
            value={bankForm.name}
            onChange={(e) => setBankForm({ ...bankForm, name: e.target.value })}
            placeholder="e.g. Kotak Mahindra"
          />
          <input
            type="number"
            step="0.01"
            min="0"
            max="20"
            data-testid="admin-bank-add-commission-input"
            value={bankForm.default_commission_percent}
            onChange={(e) => setBankForm({ ...bankForm, default_commission_percent: e.target.value })}
            placeholder="Default %"
            style={{ maxWidth: 110 }}
          />
          <button className="primary-button" onClick={addBank} data-testid="admin-bank-add-button">
            <Plus size={15} /> Add
          </button>
        </div>
        {data.banks.map((x) => {
          const assignedBoe = x.assigned_boe_id
            ? (data.users || []).find((u) => u.id === x.assigned_boe_id)
            : null;
          return (
          <div className="config-row" key={x.bank_id} data-testid={`admin-bank-row-${x.bank_id}`}>
            <div>
              <strong>{x.name}</strong>
              <small>
                {x.active === false ? "Inactive" : "Active"} · {x.bank_id}
                {x.default_commission_percent ? ` · default ${x.default_commission_percent}%` : ""}
                {assignedBoe
                  ? ` · routes to ${assignedBoe.name}`
                  : " · not routed (BOM will assign)"}
              </small>
            </div>
            <div className="row-actions">
              <button
                className="icon-button"
                onClick={() => setEditingBank(x)}
                data-testid={`admin-bank-edit-${x.bank_id}`}
                aria-label="Edit"
              >
                <Pencil size={14} />
              </button>
              <button
                className="icon-button danger-icon"
                onClick={() => deleteBank(x)}
                data-testid={`admin-bank-delete-${x.bank_id}`}
                aria-label="Delete"
              >
                <Trash2 size={14} />
              </button>
            </div>
          </div>
          );
        })}
      </section>

      <section className="panel config-list">
        <div className="section-title">
          <h3>Products</h3>
          <span className="mono">{data.products.length}</span>
        </div>
        <div className="config-add product-add">
          <input
            data-testid="admin-product-name-input"
            value={product.name}
            onChange={(e) => setProduct({ ...product, name: e.target.value })}
            placeholder="Product name"
          />
          <select
            data-testid="admin-product-bank-select"
            value={product.bank_id}
            onChange={(e) => setProduct({ ...product, bank_id: e.target.value })}
          >
            <option value="">Select bank</option>
            {data.banks.filter((b) => b.active !== false).map((b) => (
              <option key={b.bank_id} value={b.bank_id}>{b.name}</option>
            ))}
          </select>
          <select
            data-testid="admin-product-loantype-select"
            value={product.loan_type}
            onChange={(e) => setProduct({ ...product, loan_type: e.target.value })}
          >
            {loanTypes.map((t) => <option key={t}>{t}</option>)}
          </select>
          <button
            className="primary-button"
            onClick={addProduct}
            data-testid="admin-product-add-button"
            disabled={!product.bank_id || !product.name.trim()}
          >
            <Plus size={15} /> Add
          </button>
        </div>
        {data.products.map((x) => (
          <div className="config-row" key={x.product_id} data-testid={`admin-product-row-${x.product_id}`}>
            <div>
              <strong>{x.name}</strong>
              <small>{x.bank_name} · {x.loan_type} {x.active === false ? "· Inactive" : ""}</small>
            </div>
            <div className="row-actions">
              <button
                className="icon-button"
                onClick={() => setEditingProduct(x)}
                data-testid={`admin-product-edit-${x.product_id}`}
                aria-label="Edit"
              >
                <Pencil size={14} />
              </button>
              <button
                className="icon-button danger-icon"
                onClick={() => deleteProduct(x)}
                data-testid={`admin-product-delete-${x.product_id}`}
                aria-label="Delete"
              >
                <Trash2 size={14} />
              </button>
            </div>
          </div>
        ))}
      </section>

      {editingBank && (
        <BankEditModal
          bank={editingBank}
          users={data.users}
          onClose={() => setEditingBank(null)}
          onSaved={() => { setEditingBank(null); reload(); }}
        />
      )}
      {editingProduct && (
        <ProductEditModal
          product={editingProduct}
          banks={data.banks}
          onClose={() => setEditingProduct(null)}
          onSaved={() => { setEditingProduct(null); reload(); }}
        />
      )}
    </div>
  );
}

function BankEditModal({ bank, onClose, onSaved, users }) {
  const [form, setForm] = useState({
    name: bank.name || "",
    active: bank.active !== false,
    default_commission_percent: bank.default_commission_percent || 0,
    assigned_boe_id: bank.assigned_boe_id || "",
  });
  const [docs, setDocs] = useState(
    Array.isArray(bank.required_documents) ? bank.required_documents : []
  );
  const [busy, setBusy] = useState(false);
  const boes = (users || []).filter((u) => u.role === "back_office_executive" && u.active !== false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await http.patch(`/admin/banks/${bank.bank_id}`, {
        name: form.name.trim(),
        active: form.active,
        default_commission_percent: Number(form.default_commission_percent) || 0,
        assigned_boe_id: form.assigned_boe_id || "",
        required_documents: docs
          .filter((d) => (d.name || "").trim())
          .map((d) => ({ name: d.name.trim(), mandatory: !!d.mandatory, notes: (d.notes || "").trim() })),
      });
      notify.success("Bank updated");
      onSaved();
    } catch (err) {
      notify.error(err, "Could not update bank");
      setBusy(false);
    }
  };

  const addDoc = () => setDocs([...docs, { name: "", mandatory: true, notes: "" }]);
  const patchDoc = (i, patch) =>
    setDocs(docs.map((d, idx) => (idx === i ? { ...d, ...patch } : d)));
  const removeDoc = (i) => setDocs(docs.filter((_, idx) => idx !== i));

  return (
    <Modal title={`Edit ${bank.name}`} onClose={onClose}>
      <form className="record-form" onSubmit={submit}>
        <label>
          Bank / NBFC name
          <input
            data-testid="admin-bank-edit-name-input"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            required
          />
        </label>
        <div className="form-grid">
          <label>
            Default commission %
            <input
              type="number"
              step="0.01"
              min="0"
              max="20"
              data-testid="admin-bank-edit-commission-input"
              value={form.default_commission_percent}
              onChange={(e) => setForm({ ...form, default_commission_percent: e.target.value })}
            />
          </label>
          <label>
            Status
            <select
              data-testid="admin-bank-edit-active-select"
              value={form.active ? "active" : "inactive"}
              onChange={(e) => setForm({ ...form, active: e.target.value === "active" })}
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </label>
        </div>
        <label>
          Assigned Back Office Executive
          <select
            data-testid="admin-bank-edit-boe-select"
            value={form.assigned_boe_id}
            onChange={(e) => setForm({ ...form, assigned_boe_id: e.target.value })}
          >
            <option value="">— Unassigned (routes to Back Office Manager) —</option>
            {boes.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name} · {b.team_name || "Back office"}
              </option>
            ))}
          </select>
        </label>
        <p className="upload-note">
          Every new lead or application selecting this bank will be routed to
          the assigned executive. If the executive is inactive the case falls
          back to their Back Office Manager for manual assignment.
        </p>

        <div style={{ borderTop: "1px solid var(--border)", marginTop: 16, paddingTop: 12 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
            <strong style={{ fontSize: 13 }}>Required documents</strong>
            <button
              type="button"
              className="outline-button"
              data-testid="admin-bank-edit-add-doc"
              onClick={addDoc}
              style={{ padding: "4px 10px", fontSize: 12 }}
            >
              + Add document
            </button>
          </div>
          <p className="muted" style={{ fontSize: 12, margin: "0 0 8px" }}>
            Shared across every product/policy of this bank — set once, inherited everywhere.
          </p>
          {docs.length === 0 && (
            <p className="muted" style={{ fontSize: 12 }} data-testid="admin-bank-edit-docs-empty">
              No required documents configured yet.
            </p>
          )}
          {docs.map((d, i) => (
            <div
              key={i}
              data-testid={`admin-bank-edit-doc-row-${i}`}
              style={{ display: "grid", gridTemplateColumns: "1.4fr 90px 1.4fr 30px", gap: 8, alignItems: "center", marginBottom: 6 }}
            >
              <input
                data-testid={`admin-bank-edit-doc-name-${i}`}
                placeholder="Document name (e.g. PAN Card)"
                value={d.name}
                onChange={(e) => patchDoc(i, { name: e.target.value })}
              />
              <label style={{ display: "flex", gap: 4, alignItems: "center", fontSize: 12, color: "var(--muted)", margin: 0 }}>
                <input
                  type="checkbox"
                  data-testid={`admin-bank-edit-doc-mandatory-${i}`}
                  checked={!!d.mandatory}
                  onChange={(e) => patchDoc(i, { mandatory: e.target.checked })}
                />
                Mandatory
              </label>
              <input
                data-testid={`admin-bank-edit-doc-notes-${i}`}
                placeholder="Notes (optional)"
                value={d.notes || ""}
                onChange={(e) => patchDoc(i, { notes: e.target.value })}
              />
              <button
                type="button"
                data-testid={`admin-bank-edit-doc-remove-${i}`}
                className="icon-button danger"
                onClick={() => removeDoc(i)}
                aria-label="Remove"
              >
                <Trash2 size={14} />
              </button>
            </div>
          ))}
        </div>

        <div className="form-actions">
          <button type="button" className="outline-button" onClick={onClose}>Cancel</button>
          <button className="primary-button" disabled={busy} data-testid="admin-bank-edit-save">
            {busy ? "Saving…" : "Save changes"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function ProductEditModal({ product, banks, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: product.name || "",
    bank_id: product.bank_id || "",
    loan_type: product.loan_type || "Personal Loan",
    active: product.active !== false,
  });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await http.patch(`/admin/products/${product.product_id}`, {
        name: form.name.trim(),
        bank_id: form.bank_id,
        loan_type: form.loan_type,
        active: form.active,
      });
      notify.success("Product updated");
      onSaved();
    } catch (err) {
      notify.error(err, "Could not update product");
      setBusy(false);
    }
  };

  return (
    <Modal title={`Edit ${product.name}`} onClose={onClose}>
      <form className="record-form" onSubmit={submit}>
        <div className="form-grid">
          <label>
            Product name
            <input
              data-testid="admin-product-edit-name-input"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
            />
          </label>
          <label>
            Bank
            <select
              data-testid="admin-product-edit-bank-select"
              value={form.bank_id}
              onChange={(e) => setForm({ ...form, bank_id: e.target.value })}
              required
            >
              {banks.map((b) => (
                <option key={b.bank_id} value={b.bank_id}>{b.name}</option>
              ))}
            </select>
          </label>
          <label>
            Loan type
            <select
              data-testid="admin-product-edit-loantype-select"
              value={form.loan_type}
              onChange={(e) => setForm({ ...form, loan_type: e.target.value })}
            >
              {loanTypes.map((t) => <option key={t}>{t}</option>)}
            </select>
          </label>
          <label>
            Status
            <select
              data-testid="admin-product-edit-active-select"
              value={form.active ? "active" : "inactive"}
              onChange={(e) => setForm({ ...form, active: e.target.value === "active" })}
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </label>
        </div>
        <div className="form-actions">
          <button type="button" className="outline-button" onClick={onClose}>Cancel</button>
          <button className="primary-button" disabled={busy} data-testid="admin-product-edit-save">
            {busy ? "Saving…" : "Save changes"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

/* ---------- Statuses ---------- */
function StatusesTab({ data, reload }) {
  const [label, setLabel] = useState("");
  const [kind, setKind] = useState("lead");
  const [filter, setFilter] = useState("all"); // all | lead | disposition

  const add = async () => {
    if (!label.trim()) return;
    try {
      await http.post("/admin/statuses", {
        label: label.trim(), active: true, kind,
      });
      notify.success(`${kind === "disposition" ? "Disposition" : "Status"} added`);
      setLabel("");
      reload();
    } catch (err) {
      notify.error(err, "Could not add");
    }
  };

  const toggle = async (s) => {
    try {
      await http.patch(`/admin/statuses/${s.status_id}`, {
        label: s.label, active: !s.active, kind: s.kind || "lead",
      });
      reload();
    } catch (err) {
      notify.error(err, "Could not update");
    }
  };

  const rows = data.statuses
    .map((s) => ({ ...s, kind: s.kind || "lead" }))
    .filter((s) => filter === "all" || s.kind === filter);

  return (
    <section className="panel config-list" data-testid="statuses-tab-panel">
      <div className="section-title">
        <h3>Statuses & dispositions</h3>
        <span className="mono">{rows.length}</span>
      </div>
      <p className="muted" style={{ fontSize: 12, margin: "-6px 0 10px" }}>
        Lead statuses drive the Login Status pipeline. Dispositions power the calling workflow — Sales Associates pick from the active list on every call.
      </p>

      <div style={{ display: "flex", gap: 6, marginBottom: 12 }}>
        {[
          ["all", "All"],
          ["lead", "Lead statuses"],
          ["disposition", "Dispositions"],
        ].map(([k, l]) => (
          <button
            key={k}
            type="button"
            className={`chip ${filter === k ? "chip-active" : ""}`}
            onClick={() => setFilter(k)}
            data-testid={`admin-status-filter-${k}`}
          >{l}</button>
        ))}
      </div>

      <div className="config-add">
        <select
          data-testid="admin-status-kind"
          value={kind}
          onChange={(e) => setKind(e.target.value)}
          style={{ minWidth: 130 }}
        >
          <option value="lead">Lead status</option>
          <option value="disposition">Disposition</option>
        </select>
        <input
          data-testid="admin-status-add-button-input"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder={kind === "disposition" ? "e.g. Language Barrier" : "e.g. Awaiting bank query"}
        />
        <button className="primary-button" onClick={add} data-testid="admin-status-add-button">
          <Plus size={15} /> Add
        </button>
      </div>
      {rows.map((s) => (
        <div className="config-row" key={s.status_id} data-testid={`admin-status-row-${s.status_id}`}>
          <div>
            <strong>{s.label}</strong>
            <small>
              <span className={`chip ${s.kind === "disposition" ? "chip-amber" : "chip-muted"}`} style={{ marginRight: 8 }}>
                {s.kind === "disposition" ? "Disposition" : "Lead status"}
              </span>
              {s.active ? "Active" : "Inactive"}
            </small>
          </div>
          <button className="outline-button" onClick={() => toggle(s)}>
            {s.active ? "Deactivate" : "Activate"}
          </button>
        </div>
      ))}
    </section>
  );
}


/* ---------- Bank load board ---------- */
function LoadBoardTab() {
  const [rows, setRows] = useState(null);
  const [sort, setSort] = useState({ key: "active_leads", dir: "desc" });

  useEffect(() => {
    http.get("/admin/bank-load")
      .then((r) => setRows(r.data.banks))
      .catch((err) => notify.error(err, "Could not load bank workload"));
  }, []);

  if (!rows) return <Loading>Building load board…</Loading>;

  const sorted = [...rows].sort((a, b) => {
    const av = a[sort.key] ?? -1;
    const bv = b[sort.key] ?? -1;
    if (av === bv) return 0;
    return sort.dir === "asc" ? (av > bv ? 1 : -1) : (av < bv ? 1 : -1);
  });

  const flip = (key) =>
    setSort((s) => ({ key, dir: s.key === key && s.dir === "desc" ? "asc" : "desc" }));

  const totalActive = rows.reduce((s, x) => s + (x.active_leads || 0), 0);
  const unrouted = rows.filter((x) => !x.assigned_boe_name).length;
  const inactiveBoe = rows.filter((x) => x.assigned_boe_name && x.assigned_boe_active === false).length;

  return (
    <section className="panel" data-testid="admin-load-board">
      <PanelHead
        title="Bank load board"
        subtitle="Where are the leads sitting right now and how fast are they moving through?"
      />
      <div className="report-kpis">
        <div><span>Total active leads</span><strong>{totalActive}</strong></div>
        <div><span>Banks without BOE</span><strong className={unrouted ? "amber-text" : ""}>{unrouted}</strong></div>
        <div><span>Assigned BOE inactive</span><strong className={inactiveBoe ? "red-text" : ""}>{inactiveBoe}</strong></div>
        <div><span>Banks</span><strong>{rows.length}</strong></div>
      </div>
      <table className="tight-table" data-testid="load-board-table">
        <thead>
          <tr>
            <th>Bank / NBFC</th>
            <th>Assigned BOE</th>
            <SortableTh label="Active" k="active_leads" sort={sort} onClick={flip} />
            <SortableTh label="Total" k="total_leads" sort={sort} onClick={flip} />
            <SortableTh label="Approved" k="approved" sort={sort} onClick={flip} />
            <SortableTh label="Disbursed" k="disbursed" sort={sort} onClick={flip} />
            <SortableTh label="Avg days → status" k="avg_days_to_status" sort={sort} onClick={flip} />
          </tr>
        </thead>
        <tbody>
          {sorted.map((r) => (
            <tr key={r.bank_id} data-testid={`load-row-${r.bank_id}`} className={r.active === false ? "muted-row" : ""}>
              <td>
                <strong>{r.bank_name}</strong>
                {r.active === false && <> · <small className="muted">Inactive</small></>}
              </td>
              <td>
                {r.assigned_boe_name ? (
                  <>
                    {r.assigned_boe_name}
                    {r.assigned_boe_active === false && <small className="red-text"> · inactive</small>}
                  </>
                ) : (
                  <span className="amber-text">— routes to BOM —</span>
                )}
              </td>
              <td><strong>{r.active_leads}</strong></td>
              <td>{r.total_leads}</td>
              <td>{r.approved}</td>
              <td>{r.disbursed}</td>
              <td>{r.avg_days_to_status != null ? `${r.avg_days_to_status}d` : "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

function SortableTh({ label, k, sort, onClick }) {
  const active = sort.key === k;
  return (
    <th
      onClick={() => onClick(k)}
      style={{ cursor: "pointer", whiteSpace: "nowrap" }}
      data-testid={`load-board-sort-${k}`}
    >
      {label} {active ? (sort.dir === "asc" ? "↑" : "↓") : ""}
    </th>
  );
}

/* ---------- Morning digest ---------- */
function DigestTab() {
  const [managers, setManagers] = useState(null);
  const [selectedId, setSelectedId] = useState("");
  const [preview, setPreview] = useState(null);
  const [previewBusy, setPreviewBusy] = useState(false);
  const [sending, setSending] = useState(false);
  const [status, setStatus] = useState(null);
  const [lastResult, setLastResult] = useState(null);

  const loadManagers = () =>
    http.get("/admin/digest/managers")
      .then((r) => {
        setManagers(r.data.managers || []);
        if (!selectedId && r.data.managers?.length) {
          setSelectedId(r.data.managers[0].id);
        }
      })
      .catch((err) => notify.error(err, "Could not load managers"));

  const loadStatus = () =>
    http.get("/admin/digest/status")
      .then((r) => setStatus(r.data.last_run_today || null))
      .catch(() => setStatus(null));

  useEffect(() => {
    loadManagers();
    loadStatus();
  }, []);

  useEffect(() => {
    if (!selectedId) { setPreview(null); return; }
    setPreviewBusy(true);
    http.get(`/admin/digest/preview?manager_id=${encodeURIComponent(selectedId)}`)
      .then((r) => setPreview(r.data))
      .catch((err) => notify.error(err, "Preview failed"))
      .finally(() => setPreviewBusy(false));
  }, [selectedId]);

  const sendNow = async () => {
    if (sending) return;
    if (!window.confirm("Send the morning digest to every manager right now?")) return;
    setSending(true);
    try {
      const r = await http.post("/admin/digest/send-now");
      setLastResult(r.data);
      setStatus(r.data);
      const { sent = 0, skipped = 0, errors = 0 } = r.data.totals || {};
      notify.success(`Digest run finished — sent ${sent}, skipped ${skipped}, errors ${errors}`);
    } catch (err) {
      notify.error(err, "Digest send failed");
    } finally {
      setSending(false);
    }
  };

  if (managers === null) return <Loading>Loading digest settings…</Loading>;

  const runToShow = lastResult || status;

  return (
    <section className="digest-tab" data-testid="digest-tab-panel">
      <PanelHead
        title="Morning digest"
        subtitle="Sent to every Sales Manager and Back Office Manager. Fires automatically at 09:30 IST daily and can be triggered on demand."
      />
      <div className="digest-actions" style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center", marginBottom: 16 }}>
        <button
          data-testid="digest-send-now"
          className="primary-button"
          disabled={sending || managers.length === 0}
          onClick={sendNow}
        >
          {sending ? "Sending…" : "Send digest now"}
        </button>
        <span className="muted" style={{ fontSize: 13 }}>
          {managers.length} manager{managers.length === 1 ? "" : "s"} eligible
        </span>
        {runToShow && (
          <span className="muted" style={{ fontSize: 13 }} data-testid="digest-last-run">
            Last run today: {runToShow.totals?.sent ?? 0} sent · {runToShow.totals?.skipped ?? 0} skipped · {runToShow.totals?.errors ?? 0} errors
          </span>
        )}
      </div>

      {runToShow?.results?.length ? (
        <div className="table-wrap" style={{ marginBottom: 20 }}>
          <table data-testid="digest-run-results">
            <thead>
              <tr>
                <th>Manager</th><th>Email</th><th>Role</th>
                <th>Overdue</th><th>Today</th><th>Idle</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {runToShow.results.map((r) => (
                <tr key={r.manager_id}>
                  <td>{r.name}</td>
                  <td>{r.email}</td>
                  <td>{roleLabels[r.role] || r.role}</td>
                  <td>{r.counts?.overdue ?? 0}</td>
                  <td>{r.counts?.today ?? 0}</td>
                  <td>{r.counts?.idle ?? 0}</td>
                  <td>
                    {r.status === "sent" && <span className="status approved">Sent</span>}
                    {r.status === "skipped" && <span className="status documents-pending" title={r.reason}>Skipped</span>}
                    {r.status === "error" && <span className="status rejected" title={r.error}>Error</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      <div className="record-form">
        <label>
          Preview digest for
          <select
            data-testid="digest-preview-select"
            value={selectedId}
            onChange={(e) => setSelectedId(e.target.value)}
          >
            {managers.map((m) => {
              const label = `${m.name} — ${roleLabels[m.role] || m.role} · ${m.email}`;
              return <option key={m.id} value={m.id}>{label}</option>;
            })}
          </select>
        </label>
      </div>

      {previewBusy && <p className="muted" style={{ marginTop: 12 }}>Rendering preview…</p>}
      {preview && (
        <div style={{ marginTop: 16 }} data-testid="digest-preview">
          <p className="muted" style={{ fontSize: 12 }}>
            Overdue: {preview.counts?.overdue} · Due today: {preview.counts?.today} · Idle {preview.idle_days}d+: {preview.counts?.idle}
          </p>
          <iframe
            title="Digest preview"
            srcDoc={preview.html}
            style={{ width: "100%", height: 620, border: "1px solid #eee", borderRadius: 6, background: "#fff" }}
          />
        </div>
      )}
    </section>
  );
}


/* ---------- Companies master ---------- */
function CompaniesTab({ me, data }) {
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [showInactive, setShowInactive] = useState(false);
  const [categories, setCategories] = useState([]);
  const [busyId, setBusyId] = useState(null);
  const [expanded, setExpanded] = useState(null);

  const canManage = ["admin", "business_head", "back_office_manager"].includes(me?.role);
  const canDelete = me?.role === "admin";
  const banks = data?.banks || [];
  const products = data?.products || [];

  const load = () => {
    const params = new URLSearchParams();
    if (q.trim()) params.set("q", q.trim());
    if (!showInactive) params.set("active_only", "true");
    return http.get(`/admin/companies?${params.toString()}`)
      .then((r) => setRows(r.data.companies || []))
      .catch((err) => notify.error(err, "Could not load companies"));
  };

  useEffect(() => {
    http.get("/company-categories")
      .then((r) => setCategories(r.data.categories || []))
      .catch(() => setCategories([]));
  }, []);

  useEffect(() => { load(); }, [showInactive]);   // eslint-disable-line

  const submitNew = async (payload, reset) => {
    try {
      await http.post("/admin/companies", payload);
      notify.success(`Added ${payload.name}`);
      reset();
      load();
    } catch (err) {
      notify.error(err, "Could not add company");
    }
  };

  const saveRow = async (row, patch) => {
    setBusyId(row.company_id);
    try {
      await http.patch(`/admin/companies/${row.company_id}`, patch);
      notify.success(`Updated ${row.name}`);
      load();
    } catch (err) {
      notify.error(err, "Update failed");
    } finally {
      setBusyId(null);
    }
  };

  const deleteRow = async (row) => {
    if (!window.confirm(`Delete '${row.name}'? Cannot be undone.`)) return;
    setBusyId(row.company_id);
    try {
      await http.delete(`/admin/companies/${row.company_id}`);
      notify.success(`Deleted ${row.name}`);
      load();
    } catch (err) {
      notify.error(err, "Delete failed");
    } finally {
      setBusyId(null);
    }
  };

  if (rows === null) return <Loading>Loading companies…</Loading>;

  return (
    <section className="companies-tab" data-testid="companies-tab-panel">
      <PanelHead
        title="Companies master"
        subtitle="Map each employer to its lender-specific grade for every bank + loan product. The category is A-Z (not an industry) — Sales Associates see it auto-populated based on Company + Bank + Product."
      />

      {canManage && <CompanyAddForm onSubmit={submitNew} />}

      {canManage && <CompanyBulkImport onDone={load} />}

      <div className="companies-toolbar" style={{ display: "flex", gap: 12, alignItems: "center", margin: "16px 0 12px" }}>
        <input
          data-testid="companies-search"
          placeholder="Search company…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") load(); }}
          style={{ padding: "8px 12px", border: "1px solid var(--border)", background: "var(--surface)", minWidth: 260 }}
        />
        <button className="outline-button" onClick={load}>Search</button>
        <label style={{ display: "flex", gap: 6, alignItems: "center", fontSize: 13, color: "var(--muted)" }}>
          <input
            type="checkbox"
            checked={showInactive}
            onChange={(e) => setShowInactive(e.target.checked)}
          />
          Show inactive
        </label>
        <span className="muted" style={{ fontSize: 12 }}>{rows.length} shown</span>
      </div>

      <div className="table-wrap">
        <table data-testid="companies-table">
          <thead>
            <tr>
              <th>Company</th>
              <th>Lender mappings</th>
              <th>Status</th>
              <th style={{ textAlign: "right" }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={4} className="muted" style={{ padding: 16 }}>No companies match — add one above.</td></tr>
            )}
            {rows.map((row) => (
              <CompanyRow
                key={row.company_id}
                row={row}
                categories={categories}
                banks={banks}
                products={products}
                busy={busyId === row.company_id}
                canManage={canManage}
                canDelete={canDelete}
                onSave={saveRow}
                onDelete={deleteRow}
                onReload={load}
                expanded={expanded === row.company_id}
                onToggle={() => setExpanded(expanded === row.company_id ? null : row.company_id)}
              />
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function CompanyAddForm({ onSubmit }) {
  const [name, setName] = useState("");
  const reset = () => { setName(""); };
  const submit = (e) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) return;
    onSubmit({ name: trimmed, active: true, mappings: [] }, reset);
  };
  return (
    <form className="record-form" onSubmit={submit} data-testid="company-add-form" style={{ background: "var(--surface)", padding: 16, border: "1px solid var(--border)" }}>
      <div style={{ display: "grid", gridTemplateColumns: "2fr auto", gap: 12, alignItems: "end" }}>
        <label>
          Company name
          <input
            data-testid="company-add-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Infosys"
            required
          />
          <small className="muted" style={{ fontSize: 11 }}>
            Add the company first, then expand its row to add Bank/NBFC + Product + Category (A-Z) mappings.
          </small>
        </label>
        <button className="primary-button" type="submit" data-testid="company-add-submit">
          Add company
        </button>
      </div>
    </form>
  );
}

function CompanyBulkImport({ onDone }) {
  const [rows, setRows] = useState([]);
  const [errors, setErrors] = useState([]);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  const parseCsv = (text) => {
    // 4 columns: Company Name, Bank Name, Loan Product, Category (A-Z).
    // Legacy 3-col rows still parse; missing product will 400 server-side.
    const out = [];
    const errs = [];
    const lines = text.replace(/\r\n?/g, "\n").split("\n").filter((l) => l.trim());
    if (!lines.length) return { rows: [], errors: ["File is empty."] };
    let startIdx = 0;
    const first = lines[0].toLowerCase();
    if (first.includes("name") && (first.includes("category") || first.includes("bank") || first.includes("product") || first.includes("type"))) {
      startIdx = 1;
    }
    for (let i = startIdx; i < lines.length; i++) {
      const raw = lines[i];
      const parts = [];
      let cur = "";
      let inQuotes = false;
      for (let ch = 0; ch < raw.length; ch++) {
        const c = raw[ch];
        if (c === '"') { inQuotes = !inQuotes; continue; }
        if (c === "," && !inQuotes) { parts.push(cur); cur = ""; continue; }
        cur += c;
      }
      parts.push(cur);
      const name = (parts[0] || "").trim();
      const bank_name = (parts[1] || "").trim();
      const product_name = (parts[2] || "").trim();
      const category = (parts[3] || "").trim().toUpperCase();
      if (!name) {
        errs.push(`Line ${i + 1}: missing company name — skipped.`);
        continue;
      }
      out.push({ name, bank_name, product_name, category, active: true });
    }
    return { rows: out, errors: errs };
  };

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 512 * 1024) {
      notify.error(null, "CSV too large — max 512KB (~5000 rows).");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const { rows: parsed, errors: errs } = parseCsv(String(reader.result || ""));
      setRows(parsed);
      setErrors(errs);
      if (!parsed.length) {
        notify.error(null, "No importable rows found in that CSV.");
      }
    };
    reader.readAsText(file);
  };

  const upload = async () => {
    if (!rows.length || busy) return;
    setBusy(true);
    try {
      const r = await http.post("/admin/companies/bulk", rows);
      const errs = r.data.errors || [];
      const ic = r.data.inserted_companies || 0;
      const am = r.data.added_mappings || 0;
      const sk = r.data.skipped || 0;
      if (errs.length) {
        notify.info(`+${ic} companies · +${am} mappings · ${sk} skipped (${errs.length} row issues)`);
      } else {
        notify.success(`+${ic} companies · +${am} mappings · ${sk} skipped`);
      }
      setRows([]);
      setErrors(errs.map((e) => `Row ${e.row}: ${e.error}`));
      if (inputRef.current) inputRef.current.value = "";
      onDone();
    } catch (err) {
      notify.error(err, "Bulk import failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="record-form"
      data-testid="companies-bulk-import"
      style={{ marginTop: 12, padding: 16, border: "1px dashed var(--border)", background: "var(--surface)" }}
    >
      <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
        <div style={{ flex: "1 1 260px" }}>
          <strong style={{ fontSize: 13 }}>Bulk import companies (CSV)</strong>
          <p className="muted" style={{ fontSize: 12, margin: "4px 0 0" }}>
            Four columns per row: <code>Company Name, Bank/NBFC, Loan Product, Category (A-Z)</code>. Header row optional. Lender is matched case-insensitively — <code>HDFC</code>, <code>HDFC Bank</code>, <code>ICICI</code>, <code>BAJAJ</code> etc. all resolve automatically. Rows without a valid lender are skipped and reported (never silently imported).
          </p>
          <button
            type="button"
            className="link-button"
            data-testid="companies-bulk-download-template"
            onClick={() => {
              const csv = "Company Name,Bank/NBFC,Loan Product,Category\nInfosys,HDFC Bank,Personal Loan,A\nInfosys,ICICI Bank,Personal Loan,B\nTCS,Bajaj Finserv,Personal Loan,A\nTCS,Axis Bank,Personal Loan,C\n";
              const blob = new Blob([csv], { type: "text/csv" });
              const url = URL.createObjectURL(blob);
              const a = document.createElement("a");
              a.href = url;
              a.download = "companies-template.csv";
              a.click();
              URL.revokeObjectURL(url);
            }}
            style={{ fontSize: 12, marginTop: 6, background: "none", border: "none", color: "var(--accent)", cursor: "pointer", padding: 0 }}
          >
            Download sample template
          </button>
        </div>
        <input
          ref={inputRef}
          type="file"
          accept=".csv,text/csv"
          onChange={onFile}
          data-testid="companies-bulk-file"
          style={{ fontSize: 12 }}
        />
        <button
          className="primary-button"
          disabled={!rows.length || busy}
          onClick={upload}
          data-testid="companies-bulk-upload"
        >
          {busy ? "Importing…" : rows.length ? `Import ${rows.length} row${rows.length === 1 ? "" : "s"}` : "Import"}
        </button>
      </div>
      {rows.length > 0 && (
        <p className="muted" style={{ fontSize: 12, marginTop: 8 }} data-testid="companies-bulk-preview">
          Preview: {rows.slice(0, 3).map((r) => r.name).join(", ")}
          {rows.length > 3 ? `, +${rows.length - 3} more` : ""}
        </p>
      )}
      {errors.length > 0 && (
        <ul className="muted" style={{ fontSize: 11, margin: "6px 0 0 18px", color: "var(--amber)" }}>
          {errors.slice(0, 5).map((e, i) => <li key={i}>{e}</li>)}
          {errors.length > 5 && <li>…and {errors.length - 5} more.</li>}
        </ul>
      )}
    </div>
  );
}



function CompanyRow({ row, categories, banks, products, busy, canManage, canDelete, onSave, onDelete, onReload, expanded, onToggle }) {
  const [editingName, setEditingName] = useState(false);
  const [draftName, setDraftName] = useState(row.name);
  const mappings = row.mappings || [];

  useEffect(() => setDraftName(row.name), [row.name]);

  const saveName = () => {
    if (draftName.trim() && draftName.trim() !== row.name) {
      onSave(row, { name: draftName.trim() });
    }
    setEditingName(false);
  };

  return (
    <>
      <tr data-testid={`company-row-${row.company_id}`}>
        <td>
          {editingName
            ? (
              <div style={{ display: "flex", gap: 6 }}>
                <input
                  data-testid={`company-name-input-${row.company_id}`}
                  value={draftName}
                  onChange={(e) => setDraftName(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") saveName(); if (e.key === "Escape") { setEditingName(false); setDraftName(row.name); } }}
                  autoFocus
                />
                <button className="primary-button compact" onClick={saveName}>Save</button>
              </div>
            )
            : <strong>{row.name}</strong>}
        </td>
        <td>
          {mappings.length === 0
            ? <span className="muted">No lender mappings yet — expand to add</span>
            : (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }} data-testid={`company-mappings-summary-${row.company_id}`}>
                {mappings.slice(0, 4).map((m, i) => (
                  <span key={i} className="chip chip-muted" style={{ fontSize: 11 }}>
                    {m.bank_name} · {m.product_name} · <strong>{m.category}</strong>
                  </span>
                ))}
                {mappings.length > 4 && (
                  <span className="chip chip-muted" style={{ fontSize: 11 }}>+{mappings.length - 4} more</span>
                )}
              </div>
            )}
        </td>
        <td>
          {row.active
            ? <span className="status approved">Active</span>
            : <span className="status rejected">Inactive</span>}
        </td>
        <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
          {canManage && (
            <button
              className="outline-button compact"
              disabled={busy}
              data-testid={`company-toggle-${row.company_id}`}
              onClick={onToggle}
            >
              {expanded ? "Close" : `Mappings (${mappings.length})`}
            </button>
          )}
          {canManage && !editingName && (
            <button className="outline-button compact" style={{ marginLeft: 6 }} disabled={busy} onClick={() => setEditingName(true)}>Rename</button>
          )}
          {canManage && (
            <button
              className="outline-button compact"
              style={{ marginLeft: 6 }}
              disabled={busy}
              onClick={() => onSave(row, { active: !row.active })}
              data-testid={`company-activate-${row.company_id}`}
            >
              {row.active ? "Deactivate" : "Activate"}
            </button>
          )}
          {canDelete && (
            <button
              className="outline-button compact"
              style={{ marginLeft: 6, color: "var(--red)" }}
              disabled={busy}
              onClick={() => onDelete(row)}
              data-testid={`company-delete-${row.company_id}`}
            >
              Delete
            </button>
          )}
        </td>
      </tr>
      {expanded && (
        <tr>
          <td colSpan={4} style={{ background: "var(--alt)" }}>
            <CompanyMappingsEditor
              company={row}
              banks={banks}
              products={products}
              categories={categories}
              onReload={onReload}
            />
          </td>
        </tr>
      )}
    </>
  );
}

function CompanyMappingsEditor({ company, banks, products, categories, onReload }) {
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ bank_id: "", product_id: "", category: "" });
  const activeBanks = (banks || []).filter((b) => b.active !== false);
  const bankProducts = (products || []).filter((p) => p.bank_id === form.bank_id && p.active !== false);

  const add = async () => {
    if (!form.bank_id || !form.product_id || !form.category) {
      notify.error(null, "Bank, Product and Category are required.");
      return;
    }
    setBusy(true);
    try {
      await http.post(`/admin/companies/${company.company_id}/mappings`, form);
      notify.success("Mapping added");
      setForm({ bank_id: "", product_id: "", category: "" });
      onReload();
    } catch (err) {
      notify.error(err, "Could not add mapping");
    } finally {
      setBusy(false);
    }
  };

  const updateCategory = async (idx, newCategory) => {
    setBusy(true);
    try {
      await http.patch(`/admin/companies/${company.company_id}/mappings/${idx}`, { category: newCategory });
      onReload();
    } catch (err) {
      notify.error(err, "Update failed");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (idx) => {
    if (!window.confirm("Remove this lender mapping?")) return;
    setBusy(true);
    try {
      await http.delete(`/admin/companies/${company.company_id}/mappings/${idx}`);
      onReload();
    } catch (err) {
      notify.error(err, "Remove failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid={`company-mappings-editor-${company.company_id}`} style={{ padding: 12 }}>
      <p className="eyebrow" style={{ margin: 0 }}>LENDER MAPPINGS</p>
      <p className="muted" style={{ fontSize: 12, margin: "4px 0 10px" }}>
        Add one row per <strong>Bank/NBFC + Loan Product</strong>. Category is the lender-specific grade (A-Z).
      </p>

      {(company.mappings || []).length > 0 && (
        <table style={{ width: "100%", marginBottom: 12 }} data-testid={`company-mappings-list-${company.company_id}`}>
          <thead>
            <tr>
              <th>Bank / NBFC</th>
              <th>Loan Product</th>
              <th>Category</th>
              <th style={{ textAlign: "right" }}></th>
            </tr>
          </thead>
          <tbody>
            {(company.mappings || []).map((m, i) => (
              <tr key={i} data-testid={`company-mapping-${company.company_id}-${i}`}>
                <td>{m.bank_name}</td>
                <td>{m.product_name}</td>
                <td>
                  <select
                    data-testid={`company-mapping-category-${company.company_id}-${i}`}
                    value={m.category}
                    onChange={(e) => updateCategory(i, e.target.value)}
                    disabled={busy}
                  >
                    {categories.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </td>
                <td style={{ textAlign: "right" }}>
                  <button
                    className="outline-button compact"
                    style={{ color: "var(--red)" }}
                    onClick={() => remove(i)}
                    disabled={busy}
                    data-testid={`company-mapping-remove-${company.company_id}-${i}`}
                  >
                    Remove
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "1.4fr 1.4fr 90px auto", gap: 10, alignItems: "end" }}>
        <label>
          Bank / NBFC
          <select
            data-testid={`company-mapping-add-bank-${company.company_id}`}
            value={form.bank_id}
            onChange={(e) => setForm({ ...form, bank_id: e.target.value, product_id: "" })}
          >
            <option value="">— Select —</option>
            {activeBanks.map((b) => <option key={b.bank_id} value={b.bank_id}>{b.name}</option>)}
          </select>
        </label>
        <label>
          Loan Product
          <select
            data-testid={`company-mapping-add-product-${company.company_id}`}
            value={form.product_id}
            onChange={(e) => setForm({ ...form, product_id: e.target.value })}
            disabled={!form.bank_id}
          >
            <option value="">— Select —</option>
            {bankProducts.map((p) => <option key={p.product_id} value={p.product_id}>{p.name}</option>)}
          </select>
        </label>
        <label>
          Category
          <select
            data-testid={`company-mapping-add-category-${company.company_id}`}
            value={form.category}
            onChange={(e) => setForm({ ...form, category: e.target.value })}
          >
            <option value="">A–Z</option>
            {categories.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </label>
        <button
          className="primary-button"
          onClick={add}
          disabled={busy || !form.bank_id || !form.product_id || !form.category}
          data-testid={`company-mapping-add-${company.company_id}`}
        >
          + Add mapping
        </button>
      </div>
    </div>
  );
}

